/* eslint-disable react/prop-types */
import { useEffect, useMemo, useRef, useState } from "react";
import { toJpeg } from "html-to-image";
import { toast } from "sonner";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ChevronLeft, ChevronRight, Download, GalleryHorizontal, ImagePlus, Instagram,
  Loader2, MoveHorizontal, Plus, Save, Sparkles, Trash2, X,
} from "lucide-react";
import { api } from "@/lib/api";
import CarouselImageDialog from "@/components/CarouselImageDialog";
import PhotoStage from "@/components/PhotoStage";
import { ScheduleControls } from "@/components/ScheduledPostsList";
import { formatInTz, propertyTimezone } from "@/lib/timezone";
import {
  ALIGNS, CHIP_MAX, FIT_WIDTH, KINDS, LIME, MAX_SLIDES, MAX_ZOOM, MIN_SLIDES,
  MIN_ZOOM, PAD, SLIDE_H, SLIDE_W, STYLES, ZONE_FILL, addSlide,
  contentTop, coverChip, creditPatch, defaultCredit, drawnCredit, fitFont,
  frameRect, imageOptions, itemsFromText,
  linePath, manualRect, moveSlide, normalizeStyle, panoramaCredit,
  panoramaRect, pendingSourceImports, photoBox, photoCrop, publishProblem,
  rectToPlacement, removeSlide, slideImage,
  slidePhotoRect, slideStyle, slideViewBox, stripWidth, updateSlide, zonePath,
} from "@/lib/carouselLayout";
import { tr } from "@/lib/i18n";
import { trRich } from "@/lib/i18nRich";

// Instagram carousel (backend/carousel.py, routes/carousel.py).
// The carousel has a style; any slide can override it. One continuous
// lime line runs across all slides — see lib/carouselLayout.js for the
// geometry. A panorama (one wide photo) can span every slide the same
// way; any slide photo can be sized by hand (drag its corners).

const THUMB_W = 216; // on-screen slide preview width (full size is 1080)
const SLIDE_STAGE_SCALE = 0.2;   // per-slide photo editor
const SLIDE_STAGE_MARGIN = 400;  // slide px shown around the slide
const PANO_STAGE_WIDTH = 760;    // on-screen width of the panorama editor
const PANO_MARGIN_X = 540;
const PANO_MARGIN_Y = 360;

/** Natural width ÷ height of each image URL, once loaded. */
function useImageAspects(urls) {
  const [map, setMap] = useState({});
  const key = urls.filter(Boolean).join("|");
  useEffect(() => {
    let cancelled = false;
    urls.filter((u) => u && !map[u]).forEach((url) => {
      const img = new Image();
      img.onload = () => {
        if (!cancelled && img.naturalWidth && img.naturalHeight) {
          setMap((m) => ({ ...m, [url]: img.naturalWidth / img.naturalHeight }));
        }
      };
      img.src = url;
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
  return map;
}

/** Fetch every image URL once and hold it as a data URL. Canvas export on
 *  mobile Safari drops cross-origin <img> pixels; data URLs avoid the
 *  fetch entirely (same reasoning as StoryCardPanel's useImageAsDataUrl,
 *  here for a list of images). A failed fetch falls back to the URL. */
function useImagesAsDataUrls(urls) {
  const [map, setMap] = useState({});
  const key = urls.filter(Boolean).join("|");
  useEffect(() => {
    let cancelled = false;
    const todo = urls.filter((u) => u && !map[u]);
    todo.forEach(async (url) => {
      if (url.startsWith("data:")) {
        if (!cancelled) setMap((m) => ({ ...m, [url]: url }));
        return;
      }
      try {
        const r = await fetch(url, { cache: "force-cache", mode: "cors" });
        if (!r.ok) throw new Error(tr("HTTP {status}", { status: r.status }));
        const blob = await r.blob();
        const reader = new FileReader();
        reader.onload = () => {
          if (!cancelled) setMap((m) => ({ ...m, [url]: reader.result }));
        };
        reader.onerror = () => {
          if (!cancelled) setMap((m) => ({ ...m, [url]: url }));
        };
        reader.readAsDataURL(blob);
      } catch {
        if (!cancelled) setMap((m) => ({ ...m, [url]: url }));
      }
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
  return map;
}

/** Render one full-size slide node to a JPEG data URL. Same two
 *  safeguards as StoryCardPanel's captureCardPng: wait for every <img>
 *  to decode, and discard a warm-up pass (html-to-image can drop
 *  just-loaded images on the first call in Safari). JPEG keeps a
 *  ten-slide upload to a few MB; the server re-encodes to sRGB. */
async function captureSlideJpeg(node) {
  const imgs = Array.from(node.querySelectorAll("img"));
  await Promise.all(
    imgs.map(async (img) => {
      if (typeof img.decode === "function") {
        try {
          await img.decode();
        } catch {
          /* ignore */
        }
      } else if (!img.complete) {
        await new Promise((res) => {
          img.addEventListener("load", res, { once: true });
          img.addEventListener("error", res, { once: true });
        });
      }
    }),
  );
  await new Promise((res) => requestAnimationFrame(() => res()));
  const opts = {
    cacheBust: true,
    pixelRatio: 1,
    quality: 0.92,
    width: SLIDE_W,
    height: SLIDE_H,
    backgroundColor: "#0B0B0B",
    style: { transform: "scale(1)", transformOrigin: "top left" },
  };
  try {
    await toJpeg(node, opts);
  } catch {
    /* warm-up */
  }
  return toJpeg(node, opts);
}

function pickOne(list, seedKey) {
  if (!list || list.length === 0) return "";
  let h = 0;
  for (const ch of String(seedKey)) h = (h * 31 + ch.charCodeAt(0)) | 0;
  return list[Math.abs(h) % list.length];
}

function hostFromUrl(url) {
  if (!url) return "";
  try {
    return new URL(url).host.replace(/^www\./, "");
  } catch {
    return String(url).replace(/^https?:\/\//, "").replace(/\/$/, "");
  }
}

// ---------------- One slide at full size (1080×1350) --------------------

function Kicker({ children }) {
  if (!children) return null;
  return (
    <div
      className="font-black uppercase"
      style={{ color: LIME, fontSize: 32, letterSpacing: 5, marginBottom: 18 }}
    >
      {children}
    </div>
  );
}

function SlideBody({ slide, headline, tag }) {
  const title = slide.title || "";
  const titleStyle = (base, min, maxChars) => ({
    color: "#fff",
    fontSize: fitFont(title, base, min, maxChars),
    lineHeight: 1.06,
    letterSpacing: -1,
  });
  switch (slide.kind) {
    case "cover":
      return (
        <>
          <Kicker>{slide.kicker || tag}</Kicker>
          <div className="font-black uppercase" style={titleStyle(66, 42, 44)}>{title}</div>
        </>
      );
    case "stat":
      return (
        <>
          <Kicker>{slide.kicker}</Kicker>
          <div
            className="font-black"
            style={{ color: "#fff", fontSize: fitFont(slide.stat, 230, 110, 5), lineHeight: 0.92, letterSpacing: -4 }}
          >
            {slide.stat}
          </div>
          {slide.body && (
            <div style={{ color: "rgba(255,255,255,0.9)", fontSize: 40, fontWeight: 600, lineHeight: 1.3, marginTop: 28 }}>
              {slide.body}
            </div>
          )}
        </>
      );
    case "list":
      return (
        <>
          <Kicker>{slide.kicker}</Kicker>
          {title && (
            <div className="font-black uppercase" style={{ ...titleStyle(54, 38, 40), marginBottom: 30 }}>{title}</div>
          )}
          <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 18 }}>
            {(slide.items || []).map((item, i) => (
              <div
                key={i}
                className="font-black uppercase"
                style={{
                  backgroundColor: LIME, color: "#000", padding: "8px 16px",
                  fontSize: fitFont(item, 44, 32, 26), lineHeight: 1.1,
                }}
              >
                {item}
              </div>
            ))}
          </div>
        </>
      );
    case "quote":
      return (
        <>
          <div className="font-black" style={{ color: LIME, fontSize: 200, lineHeight: 0.75, height: 110 }} aria-hidden="true">
            “
          </div>
          <div style={{ color: "#fff", fontStyle: "italic", fontWeight: 600, fontSize: fitFont(slide.body, 48, 34, 110), lineHeight: 1.25 }}>
            {slide.body}
          </div>
          {slide.attribution && (
            <div className="font-mono uppercase" style={{ color: "rgba(255,255,255,0.75)", fontSize: 26, letterSpacing: 2, marginTop: 28 }}>
              {slide.attribution}
            </div>
          )}
        </>
      );
    case "closing":
      return (
        <>
          <Kicker>{slide.kicker || tr("READ THE FULL STORY")}</Kicker>
          <div className="font-black uppercase" style={{ color: "#fff", fontSize: fitFont(title || headline, 58, 40, 48), lineHeight: 1.06, letterSpacing: -1 }}>
            {title || headline}
          </div>
          <div className="font-mono" style={{ color: LIME, fontSize: 34, marginTop: 36 }}>{tr("↘ Read more · link in bio")}</div>
        </>
      );
    default:
      return (
        <>
          <Kicker>{slide.kicker}</Kicker>
          {title && <div className="font-black uppercase" style={titleStyle(56, 40, 40)}>{title}</div>}
          {slide.body && (
            <div style={{ color: "rgba(255,255,255,0.9)", fontSize: fitFont(slide.body, 38, 30, 150), fontWeight: 600, lineHeight: 1.3, marginTop: 22 }}>
              {slide.body}
            </div>
          )}
        </>
      );
  }
}

/** A photo placed at an explicit rect (hand-sized or panorama). */
function PlacedImg({ src, rect, style }) {
  return (
    <img
      src={src}
      alt=""
      crossOrigin="anonymous"
      style={{
        position: "absolute", left: rect.left, top: rect.top, width: rect.width,
        height: rect.height == null ? "auto" : rect.height,
        maxWidth: "none", maxHeight: "none", ...style,
      }}
    />
  );
}

function CarouselSlide({
  style, slide, index, total, bg, pano, logo, chip, tag, site, credit, headline, line, zone,
}) {
  const crop = photoCrop(index, slide.align);
  const isCover = index === 0;
  const isLast = index === total - 1;
  const autoStyle = {
    position: "absolute", inset: 0, width: "100%", height: "100%",
    maxWidth: "none", objectFit: "cover", objectPosition: crop.position,
    transform: `scale(${crop.scale})`, transformOrigin: crop.origin,
  };
  const box = photoBox(style, index, total);
  const frame = frameRect(index, total);
  // The photo's rect in SLIDE px when it is a panorama slice or sized by
  // hand; null = automatic placement (object-fit cover + crop).
  let placed = null;
  if (pano) {
    placed = { ...pano, left: pano.left - index * SLIDE_W };
  } else if (slide.zoom > 0) {
    const r = manualRect(slide, box, 0);
    placed = { ...r, left: r.left + box.left, top: r.top + box.top };
  }
  const inBox = placed && { ...placed, left: placed.left - box.left, top: placed.top - box.top };
  return (
    <div className="relative overflow-hidden" style={{ width: SLIDE_W, height: SLIDE_H, backgroundColor: style === "frame" ? "#111827" : "#0B0B0B" }}>
      {/* Photo layer */}
      {style === "frame" ? (
        <>
          {bg && (pano
            ? <PlacedImg src={bg} rect={placed} style={{ opacity: 0.3, filter: "blur(6px)" }} />
            : <img src={bg} alt="" crossOrigin="anonymous" style={{ ...autoStyle, opacity: 0.3, filter: "blur(6px)" }} />)}
          <div
            style={{
              position: "absolute", overflow: "hidden", top: frame.top, left: frame.left,
              width: frame.width, height: frame.height, border: `14px solid ${LIME}`,
            }}
          >
            {bg && (inBox ? <PlacedImg src={bg} rect={inBox} /> : <img src={bg} alt="" crossOrigin="anonymous" style={autoStyle} />)}
          </div>
        </>
      ) : (
        <>
          {bg && (placed ? <PlacedImg src={bg} rect={placed} /> : <img src={bg} alt="" crossOrigin="anonymous" style={autoStyle} />)}
          <div style={{ position: "absolute", inset: 0, background: "linear-gradient(180deg, rgba(0,0,0,.45) 0%, rgba(0,0,0,0) 22%)" }} />
        </>
      )}

      {/* Text zone + the continuous line (this slide's window of it) */}
      <svg width={SLIDE_W} height={SLIDE_H} viewBox={slideViewBox(index)} style={{ position: "absolute", left: 0, top: 0 }} aria-hidden="true">
        <path d={zone} fill={ZONE_FILL[style]} />
        <path d={line} fill="none" stroke={LIME} strokeWidth="6" strokeLinejoin="round" />
      </svg>

      {/* Header (no page counter: Instagram shows its own) */}
      {isCover && chip && (
        <div
          className="absolute text-white font-black uppercase"
          data-testid="carousel-chip"
          style={{ top: PAD, left: PAD, padding: "10px 20px", backgroundColor: "#DC2626", fontSize: 30, borderRadius: 7, letterSpacing: 4 }}
        >
          {chip}
        </div>
      )}
      {(isCover || isLast) && logo && (
        <img
          src={logo}
          alt={tr("logo")}
          crossOrigin="anonymous"
          className="absolute"
          style={{ top: PAD, [isCover ? "right" : "left"]: PAD, maxWidth: 300, maxHeight: 150, objectFit: "contain" }}
        />
      )}

      {/* Copy, then the photo credit in the dark zone below it */}
      <div className="absolute" style={{ left: PAD, right: PAD, top: contentTop(index, total) + (isCover ? 44 : 60) }}>
        <SlideBody slide={slide} headline={headline} tag={tag} />
        {credit && (
          <div
            className="font-mono uppercase"
            data-testid="carousel-credit"
            style={{
              color: "rgba(255,255,255,0.6)", fontSize: 20, letterSpacing: 2, marginTop: 30,
              whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
            }}
          >
            {credit}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="absolute flex items-end justify-between font-mono" style={{ left: PAD, right: PAD, bottom: 64 }}>
        <span style={{ color: "rgba(255,255,255,0.8)", fontSize: 26, letterSpacing: 3 }}>
          {isCover || isLast ? tr("SAVE THIS. SHARE IT.") : ""}
        </span>
        {isCover ? (
          <span style={{ color: LIME, fontSize: 30 }}>{tr("SWIPE →")}</span>
        ) : (
          <span className="text-white" style={{ fontSize: 28 }}>{site}</span>
        )}
      </div>
    </div>
  );
}

/** The selected slide's photo, sized by hand (per-slide mode). */
function SlidePhotoEditor({ slide, index, total, style, src, aspect, slideNode, onChange }) {
  if (!src) return null;
  if (!aspect) {
    return (
      <div className="text-[11px] text-[var(--ink-muted)] inline-flex items-center gap-1">
        <Loader2 className="w-3 h-3 animate-spin" />{" "}{tr("Loading photo…")}</div>
    );
  }
  const box = photoBox(style, index, total);
  const r = slidePhotoRect(slide, index, box, aspect);
  const rect = { left: r.left + box.left, top: r.top + box.top, width: r.width, height: r.height };
  return (
    <PhotoStage
      contentW={SLIDE_W}
      contentH={SLIDE_H}
      marginX={SLIDE_STAGE_MARGIN}
      marginY={SLIDE_STAGE_MARGIN}
      scale={SLIDE_STAGE_SCALE}
      rect={rect}
      src={src}
      unitW={box.width}
      minW={box.width * MIN_ZOOM}
      maxW={box.width * MAX_ZOOM}
      viewports={[{ key: "slide", left: 0, top: 0, width: SLIDE_W, height: SLIDE_H, node: slideNode }]}
      testid="carousel-photo-stage"
      onChange={(nr) => onChange(rectToPlacement(
        { left: nr.left - box.left, top: nr.top - box.top, width: nr.width },
        box.width,
      ))}
    />
  );
}

// ---------------- Slide field editor ------------------------------------

const FIELDS = {
  cover: ["kicker", "title"],
  stat: ["kicker", "stat", "body"],
  list: ["kicker", "title", "items"],
  quote: ["body", "attribution"],
  text: ["kicker", "title", "body"],
  closing: ["kicker", "title"],
};
const FIELD_LABEL = {
  get kicker() { return tr("Kicker (small line above)"); },
  get title() { return tr("Title"); },
  get stat() { return tr("Big number"); },
  get body() { return tr("Text"); },
  get items() { return tr("Rows (one per line, max 4)"); },
  get attribution() { return tr("Who said it"); },
};
const INPUT = "w-full px-2 py-1.5 text-sm border border-[var(--border)] bg-white outline-none focus:border-[var(--ink)]";
const TEXTAREA = "w-full px-2 py-1.5 text-sm resize-y border border-[var(--border)] bg-white outline-none focus:border-[var(--ink)]";
const BTN_SECONDARY = "inline-flex items-center gap-1 px-2 py-1 border border-[var(--border)] hover:bg-[var(--secondary)] text-sm disabled:opacity-50";
const BTN_PRIMARY = "inline-flex items-center gap-1.5 px-3 py-1.5 text-sm bg-[var(--slate-900)] text-white hover:bg-black disabled:opacity-60";
const BTN_OUTLINE = "inline-flex items-center gap-1.5 px-3 py-1.5 text-sm border border-[var(--slate-900)] text-[var(--slate-900)] hover:bg-[var(--slate-900)] hover:text-white disabled:opacity-60";

function AlignGrid({ value, manual, onChange }) {
  return (
    <div className="flex items-start gap-3">
      <div className="grid grid-cols-3 gap-1 shrink-0" role="radiogroup" aria-label={tr("Photo alignment")}>
        {ALIGNS.map((a) => (
          <button
            key={a.id}
            type="button"
            role="radio"
            aria-checked={!manual && value === a.id}
            title={a.label}
            aria-label={a.label}
            data-testid={`carousel-align-${a.id}`}
            onClick={() => onChange(!manual && value === a.id ? "" : a.id)}
            className={`w-7 h-7 border flex items-center justify-center ${
              !manual && value === a.id
                ? "bg-[var(--slate-900)] border-[var(--slate-900)]"
                : "border-[var(--border)] hover:bg-[var(--secondary)]"
            }`}
          >
            <span className={`block w-1.5 h-1.5 rounded-full ${!manual && value === a.id ? "bg-white" : "bg-[var(--ink-muted)]"}`} />
          </button>
        ))}
      </div>
      <div className="text-[11px] text-[var(--ink-muted)] space-y-1.5">
        <div>
          {manual
            ? tr("Sized by hand. Pick a position here to go back to an automatic crop.")
            : value
              ? tr("{label} — this part of the photo stays in view.", { label: ALIGNS.find((a) => a.id === value)?.label })
              : tr("Auto — each slide crops the photo differently, so one image doesn't look repeated.")}
        </div>
        {(value || manual) && (
          <button type="button" className="underline" onClick={() => onChange("")} data-testid="carousel-align-auto">{tr("Back to auto")}</button>
        )}
      </div>
    </div>
  );
}

function SlideEditor({
  slide, index, total, article, carouselStyle, imageUrl, importing,
  onChange, onPickImage, onNewImage, onMove, onRemove,
  panorama, photoSrc, photoAspect, slideNode, badge,
}) {
  const options = imageOptions(article);
  const hasCurrent = options.some((o) => o.url === imageUrl);
  const fallbackCredit = defaultCredit(slide, article, panorama);
  // While the field has focus it shows exactly what was typed (so it can
  // be emptied); otherwise it shows the slide's own text, else the
  // default, so the default credit is always there to edit.
  const [creditDraft, setCreditDraft] = useState(null);
  const creditShown = creditDraft ?? (slide.credit || fallbackCredit);
  const creditOn = !slide.credit_hidden;
  const effStyle = slideStyle(slide, carouselStyle);
  const quoteLabel = slide.kind === "quote" ? tr("Quote (exactly as in the article)") : FIELD_LABEL.body;
  return (
    <div className="space-y-3" data-testid="carousel-slide-editor">
      <div className="flex flex-wrap items-center gap-2">
        {trRich("<0>Slide {value} of {total}</0>", { value: index + 1, total }, [<span className="overline" />])}
        <div className="ml-auto flex gap-1">
          <button type="button" className={BTN_SECONDARY} onClick={() => onMove(-1)} disabled={index === 0} aria-label={tr("Move slide left")}>
            <ChevronLeft className="w-3.5 h-3.5" />
          </button>
          <button type="button" className={BTN_SECONDARY} onClick={() => onMove(1)} disabled={index === total - 1} aria-label={tr("Move slide right")}>
            <ChevronRight className="w-3.5 h-3.5" />
          </button>
          <button type="button" className={BTN_SECONDARY} onClick={onRemove} disabled={total <= MIN_SLIDES} aria-label={tr("Delete slide")}>
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="overline mb-1.5 block" htmlFor="carousel-kind">{tr("Slide type")}</label>
          <select id="carousel-kind" className={INPUT} value={slide.kind} onChange={(e) => onChange({ kind: e.target.value })}>
            {KINDS.map((k) => (
              <option key={k.id} value={k.id}>{k.label}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="overline mb-1.5 block" htmlFor="carousel-slide-style">{tr("Slide style")}</label>
          <select
            id="carousel-slide-style"
            data-testid="carousel-slide-style"
            className={INPUT}
            value={slide.style || ""}
            onChange={(e) => onChange({ style: e.target.value })}
          >
            <option value="">{tr("Same as carousel ({label})", { label: STYLES.find((s) => s.id === carouselStyle)?.label })}</option>
            {STYLES.map((s) => (
              <option key={s.id} value={s.id}>{s.label}</option>
            ))}
          </select>
        </div>
      </div>
      {index === 0 && badge && (
        <div>
          <label className="overline mb-1.5 block" htmlFor="carousel-chip">{tr("Red badge")}</label>
          <div className="flex items-center gap-3">
            <input
              id="carousel-chip"
              data-testid="carousel-chip-input"
              className={INPUT}
              list="carousel-chip-options"
              value={badge.chip}
              maxLength={CHIP_MAX}
              disabled={badge.hidden}
              placeholder={badge.auto || tr("e.g. BREAKING")}
              onChange={(e) => badge.onChange({ chip: e.target.value })}
            />
            <label className="text-xs inline-flex items-center gap-1 shrink-0">
              <input
                type="checkbox"
                data-testid="carousel-chip-hide"
                checked={badge.hidden}
                onChange={(e) => badge.onChange({ hidden: e.target.checked })}
              />{tr("Hide")}</label>
          </div>
          <datalist id="carousel-chip-options">
            {badge.options.map((o, i) => (
              <option key={`${o}-${i}`} value={o} />
            ))}
          </datalist>
          <div className="text-[11px] text-[var(--ink-muted)] mt-1">
            {badge.hidden
              ? tr("No badge on the cover.")
              : badge.auto
                ? tr("Leave blank for the automatic badge ({auto}).", { auto: badge.auto })
                : tr("Type the badge text — leave blank for no badge.")}
          </div>
        </div>
      )}
      {(FIELDS[slide.kind] || FIELDS.text).map((f) => (
        <div key={f}>
          <label className="overline mb-1.5 block" htmlFor={`carousel-${f}`}>
            {f === "body" ? quoteLabel : FIELD_LABEL[f]}
          </label>
          {f === "items" ? (
            <textarea
              id={`carousel-${f}`}
              rows={4}
              className={TEXTAREA}
              value={(slide.items || []).join("\n")}
              onChange={(e) => onChange({ items: itemsFromText(e.target.value) })}
            />
          ) : f === "body" ? (
            <textarea id={`carousel-${f}`} rows={3} className={TEXTAREA} value={slide.body || ""} onChange={(e) => onChange({ body: e.target.value })} />
          ) : (
            <input id={`carousel-${f}`} className={INPUT} value={slide[f] || ""} onChange={(e) => onChange({ [f]: e.target.value })} />
          )}
        </div>
      ))}
      {panorama ? (
        <div className="text-[11px] text-[var(--ink-muted)] border border-[var(--border)] px-3 py-2">{tr("The panorama is on — this slide shows its part of it. Size and move the panorama above, or remove it to pick a photo per slide.")}</div>
      ) : (
      <>
      <div>
        <label className="overline mb-1.5 flex items-center gap-2" htmlFor="carousel-image">{tr("Photo")}{importing && (
            <span className="normal-case tracking-normal text-[11px] text-[var(--ink-muted)] inline-flex items-center gap-1">
              <Loader2 className="w-3 h-3 animate-spin" />{" "}{tr("Preparing photo…")}</span>
          )}
        </label>
        <select
          id="carousel-image"
          data-testid="carousel-image-select"
          className={INPUT}
          value={imageUrl}
          disabled={importing}
          onChange={(e) => {
            const opt = options.find((o) => o.url === e.target.value);
            if (opt) onPickImage(opt);
          }}
        >
          {options.length === 0 && <option value="">{tr("No photo yet — add one below")}</option>}
          {!hasCurrent && imageUrl && <option value={imageUrl}>{tr("Current photo")}</option>}
          {options.map((o) => (
            <option key={o.url} value={o.url}>{o.label}</option>
          ))}
        </select>
        <button
          type="button"
          data-testid="carousel-new-image"
          className={`${BTN_SECONDARY} mt-2`}
          onClick={onNewImage}
        >
          <ImagePlus className="w-3.5 h-3.5" />{" "}{tr("New image")}</button>
      </div>
      {photoSrc && (
        <div>
          <div className="flex items-center gap-2 mb-1.5">
            {trRich("<0>Photo size</0>", null, [<span className="overline" />])}
            <button
              type="button"
              data-testid="carousel-fit-width"
              className={`${BTN_SECONDARY} ml-auto`}
              onClick={() => onChange({ ...FIT_WIDTH, align: "" })}
            >
              <MoveHorizontal className="w-3.5 h-3.5" />{" "}{tr("Fit to width")}</button>
          </div>
          <SlidePhotoEditor
            slide={slide}
            index={index}
            total={total}
            style={effStyle}
            src={photoSrc}
            aspect={photoAspect}
            slideNode={slideNode}
            onChange={(p) => onChange({ ...p, align: "" })}
          />
          <div className="text-[11px] text-[var(--ink-muted)] mt-1">{tr("Drag a corner to resize (the shape stays), drag the photo to move it. It can run past the slide edges to zoom in on one part. Fit to width resets it.")}</div>
        </div>
      )}
      <div>
        {trRich("<0>Photo alignment</0>", null, [<span className="overline mb-1.5 block" />])}
        <AlignGrid
          value={slide.align || ""}
          manual={slide.zoom > 0}
          onChange={(align) => onChange({ align, zoom: 0, x: 0, y: 0 })}
        />
      </div>
      </>
      )}
      <div>
        <div className="flex items-center gap-3 mb-1.5">
          <label className="overline block" htmlFor="carousel-credit">{tr("Photo credit")}</label>
          <label className="ml-auto inline-flex items-center gap-1.5 text-xs text-[var(--ink-muted)] cursor-pointer">
            <input
              type="checkbox"
              data-testid="carousel-credit-toggle"
              checked={creditOn}
              onChange={(e) => onChange({ credit_hidden: !e.target.checked })}
            />{tr("Show on this slide")}</label>
        </div>
        <input
          id="carousel-credit"
          data-testid="carousel-credit-input"
          className={INPUT + (creditOn ? "" : " opacity-50")}
          value={creditShown}
          maxLength={120}
          disabled={!creditOn}
          placeholder={fallbackCredit || tr("e.g. Photo: Gringo Gazette")}
          onFocus={() => setCreditDraft(slide.credit || fallbackCredit)}
          onBlur={() => setCreditDraft(null)}
          onChange={(e) => {
            setCreditDraft(e.target.value);
            onChange(creditPatch(e.target.value, fallbackCredit));
          }}
        />
        <div className="text-[11px] text-[var(--ink-muted)] mt-1 flex flex-wrap gap-x-2">
          <span>
            {!creditOn
              ? tr("Hidden — this slide shows no credit.")
              : slide.credit
                ? tr("Edited for this slide.")
                : fallbackCredit
                  ? tr("Default: the {value} own credit. Edit it for this slide, or clear it to go back to the default.", { value: panorama ? tr("panorama's") : tr("photo's") })
                  : tr("This photo has no credit yet — type one so the slide shows it.")}
          </span>
          {creditOn && slide.credit && fallbackCredit && (
            <button
              type="button"
              className="underline"
              data-testid="carousel-credit-reset"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => { setCreditDraft(null); onChange({ credit: "" }); }}
            >{tr("Back to default")}</button>
          )}
        </div>
      </div>
    </div>
  );
}

// ---------------- Main panel ---------------------------------------------

export default function CarouselPanel({ article, settings }) {
  const qc = useQueryClient();
  const savedCarousel = article.carousel || null;
  const [style, setStyle] = useState(normalizeStyle(savedCarousel?.style));
  const [slides, setSlides] = useState(savedCarousel?.slides || []);
  const [sel, setSel] = useState(0);
  const [dirty, setDirty] = useState(false);
  const [newKind, setNewKind] = useState("text");
  const [pageId, setPageId] = useState("");
  const [caption, setCaption] = useState(article.instagram_post || "");
  const [schedEnabled, setSchedEnabled] = useState(false);
  const [schedIso, setSchedIso] = useState("");
  const [sharingAll, setSharingAll] = useState(false);
  const [newImageOpen, setNewImageOpen] = useState(false);
  const [imageTarget, setImageTarget] = useState("slide"); // "slide" | "panorama"
  const [chipText, setChipText] = useState(savedCarousel?.chip || "");
  const [chipHidden, setChipHidden] = useState(!!savedCarousel?.chip_hidden);
  const [pano, setPano] = useState(savedCarousel?.panorama || null);
  const slideRefs = useRef([]);
  const importTried = useRef(new Set());

  // Follow the server copy (after Generate / Save / another tab) unless
  // the editor has unsaved changes here.
  useEffect(() => {
    if (dirty) return;
    setStyle(normalizeStyle(article.carousel?.style));
    setSlides(article.carousel?.slides || []);
    setChipText(article.carousel?.chip || "");
    setChipHidden(!!article.carousel?.chip_hidden);
    setPano(article.carousel?.panorama || null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [article.carousel?.updated_at]);

  useEffect(() => {
    if (sel > slides.length - 1) setSel(Math.max(0, slides.length - 1));
  }, [slides.length, sel]);

  const { data: connData } = useQuery({
    queryKey: ["social-connections"],
    queryFn: api.listSocialConnections,
  });
  const igPages = useMemo(
    () => (connData?.items || []).filter((p) => p.has_ig && !p.hidden),
    [connData],
  );
  useEffect(() => {
    if (pageId || igPages.length === 0) return;
    const pinned = igPages.find((p) => p.page_id === connData?.default_page_id);
    setPageId(pinned?.page_id || igPages[0].page_id);
  }, [igPages, connData, pageId]);

  const autoChip = pickOne(settings?.card_top_chips, article.id + ":c:0");
  const chip = coverChip({ chip: chipText, chipHidden }, autoChip);
  const tag = pickOne(settings?.card_headline_tags, article.id + ":t:0");
  const site = hostFromUrl(settings?.wp_url) || "";
  const logo = settings?.card_logo_url || "";
  const total = slides.length;
  const line = useMemo(() => linePath(total), [total]);
  const zone = useMemo(() => zonePath(total), [total]);

  const imageUrls = useMemo(
    () => [...new Set([...slides.map((s) => slideImage(s, article)), logo, pano?.image].filter(Boolean))],
    [slides, article, logo, pano?.image],
  );
  const dataUrls = useImagesAsDataUrls(imageUrls);
  const aspects = useImageAspects(imageUrls);
  // Every image has resolved: to a data URL, or (fetch refused) back to
  // its own URL, which still exports on desktop browsers.
  const imagesReady = imageUrls.every((u) => dataUrls[u] !== undefined);
  const ready = (u) => (u ? dataUrls[u] || u : "");

  const edit = (next) => {
    setSlides(next);
    setDirty(true);
  };

  // Source photos are copied into our storage before a slide uses them:
  // most outlets send no CORS headers, so the browser drew them black.
  const refreshArticle = () => qc.invalidateQueries({ queryKey: ["article", article.id] });
  const importSource = useMutation({
    mutationFn: (url) => api.importCarouselImage(article.id, { kind: "source", url }),
  });
  // Slides that still point at an un-copied source photo (older saves, or
  // the default photo when the article has no featured image) get it
  // copied automatically, once per URL.
  const pendingImports = pendingSourceImports(slides, article).join("|");
  useEffect(() => {
    const todo = pendingImports ? pendingImports.split("|") : [];
    todo
      .filter((u) => !importTried.current.has(u))
      .forEach((u) => {
        importTried.current.add(u);
        importSource.mutateAsync(u).then(refreshArticle).catch(() => {});
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingImports]);

  const pickImage = async (opt) => {
    const at = sel;
    if (!opt.importFrom) {
      edit(updateSlide(slides, at, { image: opt.url }));
      return;
    }
    try {
      const r = await importSource.mutateAsync(opt.importFrom);
      await refreshArticle();
      setSlides((cur) => updateSlide(cur, at, { image: r.image.url }));
      setDirty(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || tr("Could not copy that source photo"));
    }
  };

  const onServerDoc = (doc) => {
    setStyle(normalizeStyle(doc.style));
    setSlides(doc.slides || []);
    setChipText(doc.chip || "");
    setChipHidden(!!doc.chip_hidden);
    setPano(doc.panorama || null);
    setDirty(false);
    qc.invalidateQueries({ queryKey: ["article", article.id] });
  };

  const generate = useMutation({
    mutationFn: () => api.generateCarousel(article.id, { style }),
    onSuccess: (doc) => {
      onServerDoc(doc);
      setSel(0);
      toast.success(tr("Drafted {slideCount} slides — edit anything, then Save.", { slideCount: doc.slides?.length || 0 }));
    },
    onError: (e) => toast.error(e?.response?.data?.detail || e?.message || tr("Carousel generation failed")),
  });

  const save = useMutation({
    // An older slide may hold a raw source-photo URL; save its stored
    // copy instead (an empty pick stays empty = "featured image").
    mutationFn: () => api.saveCarousel(article.id, {
      style,
      slides: slides.map((s) => (s.image ? { ...s, image: slideImage(s, article) } : s)),
      chip: chipText,
      chip_hidden: chipHidden,
      panorama: pano,
    }),
    onSuccess: (doc) => {
      onServerDoc(doc);
      toast.success(tr("Carousel saved"));
    },
    onError: (e) => toast.error(e?.response?.data?.detail || e?.message || tr("Save failed")),
  });

  const captureAll = async () => {
    const out = [];
    for (let i = 0; i < slides.length; i++) {
      const node = slideRefs.current[i];
      if (!node) throw new Error(tr("Slides are not ready — try again in a moment."));
      out.push(await captureSlideJpeg(node));
    }
    return out;
  };

  const share = useMutation({
    mutationFn: async () => {
      if (!imagesReady) throw new Error(tr("Slide images are still loading — try again in a second."));
      const images = await captureAll();
      return api.shareInstagramCarousel(article.id, {
        page_id: pageId,
        caption,
        slides: images,
        // Already a UTC ISO from ScheduleControls — send as is.
        scheduled_for: schedEnabled ? schedIso : undefined,
      });
    },
    onSuccess: (r) => {
      if (r.scheduled) {
        toast.success(tr("Carousel scheduled for {when}", { when: formatInTz(r.post.scheduled_for, propertyTimezone(settings)) }));
        setSchedEnabled(false);
        qc.invalidateQueries({ queryKey: ["scheduled-posts", article.id] });
      } else {
        toast.success(tr("Posting the carousel to Instagram — usually done within a minute."));
      }
      qc.invalidateQueries({ queryKey: ["article", article.id] });
    },
    onError: (e) => toast.error(e?.response?.data?.detail || e?.message || tr("Instagram carousel failed")),
  });

  // While Meta works in the background, refresh the article so the
  // status line (step, then link or error) updates by itself.
  const status = article.carousel_publish_status;
  useEffect(() => {
    if (status?.status !== "publishing") return undefined;
    const t = setInterval(() => qc.invalidateQueries({ queryKey: ["article", article.id] }), 4000);
    return () => clearInterval(t);
  }, [status?.status, qc, article.id]);

  const shareAll = async () => {
    if (!imagesReady) {
      toast.message(tr("Preparing images… try again in a second."));
      return;
    }
    setSharingAll(true);
    try {
      const images = await captureAll();
      const slug = (article.headline || "carousel").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "").slice(0, 40);
      const files = await Promise.all(
        images.map(async (d, i) => {
          const blob = await (await fetch(d)).blob();
          return new File([blob], `${slug}-${String(i + 1).padStart(2, "0")}.jpg`, { type: "image/jpeg" });
        }),
      );
      if (navigator.canShare && navigator.canShare({ files })) {
        try {
          await navigator.share({ files, title: article.headline || tr("Carousel") });
          return;
        } catch (err) {
          if (err && (err.name === "AbortError" || err.name === "NotAllowedError")) return;
        }
      }
      files.forEach((f, i) => {
        const url = URL.createObjectURL(f);
        setTimeout(() => {
          const a = document.createElement("a");
          a.href = url;
          a.download = f.name;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          setTimeout(() => URL.revokeObjectURL(url), 2000);
        }, i * 350);
      });
    } catch (e) {
      console.error(e);
      toast.error(e?.message || tr("Could not render the slides"));
    } finally {
      setSharingAll(false);
    }
  };

  const problem = publishProblem(slides, { pageId, caption });
  const current = slides[sel];

  // Panorama: one wide photo placed across the whole strip.
  const panoAspect = pano?.image ? aspects[pano.image] : 0;
  const panoStrip = pano?.image ? panoramaRect(pano, total, panoAspect) : null;
  const setPanorama = (next) => {
    setPano(next);
    setDirty(true);
  };
  const choosePanorama = (url) => setPanorama({ image: url, credit: "", ...FIT_WIDTH });

  const renderSlide = (s, i) => (
    <CarouselSlide
      style={slideStyle(s, style)}
      slide={s}
      index={i}
      total={total}
      bg={pano?.image ? ready(pano.image) : ready(slideImage(s, article))}
      pano={panoStrip}
      logo={ready(logo)}
      chip={chip}
      tag={tag}
      site={site}
      credit={drawnCredit(s, article, pano)}
      headline={article.headline || ""}
      line={line}
      zone={zone}
    />
  );
  const openImageDialog = (target) => {
    setImageTarget(target);
    setNewImageOpen(true);
  };
  const panoOptions = imageOptions(article).filter((o) => !o.importFrom);

  return (
    <section className="bg-white border border-[var(--border)]" data-testid="carousel-panel">
      <CarouselImageDialog
        open={newImageOpen}
        onOpenChange={setNewImageOpen}
        article={article}
        purpose={imageTarget}
        onAdded={(image, library) => {
          const at = sel;
          qc.setQueryData(["article", article.id], (old) => (old ? { ...old, carousel_images: library } : old));
          refreshArticle();
          if (imageTarget === "panorama") {
            choosePanorama(image.url);
            return;
          }
          setSlides((cur) => updateSlide(cur, at, { image: image.url, credit: "" }));
          setDirty(true);
        }}
      />
      <div className="p-5 space-y-5">
        {/* Style + generate */}
        <div className="flex flex-wrap items-center gap-2">
          {trRich("<0>Style (all slides)</0>", null, [<span className="overline mr-1" />])}
          {STYLES.map((s) => (
            <button
              key={s.id}
              type="button"
              data-testid={`carousel-style-${s.id}`}
              onClick={() => {
                const overridden = slides.some((sl) => sl.style);
                if (s.id !== style || overridden) {
                  setStyle(s.id);
                  if (overridden) setSlides(slides.map((sl) => ({ ...sl, style: "" })));
                  if (slides.length) setDirty(true);
                }
              }}
              className={`px-3 py-1.5 text-xs border ${
                style === s.id
                  ? "bg-[var(--slate-900)] text-white border-[var(--slate-900)]"
                  : "border-[var(--border)] hover:bg-[var(--secondary)]"
              }`}
            >
              {s.label}
            </button>
          ))}
          <div className="ml-auto flex flex-wrap gap-2">
            <button type="button" data-testid="carousel-generate" className={slides.length ? BTN_OUTLINE : BTN_PRIMARY} onClick={() => generate.mutate()} disabled={generate.isPending}>
              {generate.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
              {slides.length ? tr("Regenerate slides") : tr("Generate slides")}
            </button>
            {slides.length > 0 && (
              <button type="button" data-testid="carousel-save" className={BTN_PRIMARY} onClick={() => save.mutate()} disabled={!dirty || save.isPending}>
                {save.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                {dirty ? tr("Save changes") : tr("Saved")}
              </button>
            )}
          </div>
        </div>
        {slides.length > 0 && (
          <div className="text-[11px] text-[var(--ink-muted)] -mt-3">{tr("Sets every slide — pick a different style for one slide in its editor below. Regenerate replaces all slides with a fresh AI draft.")}</div>
        )}

        {slides.length === 0 ? (
          <div className="text-sm text-[var(--ink-muted)]">{trRich("Pick a style, then <0>Generate slides</0>. The AI drafts 5–7 slides from this article: a cover, the key facts, and a closing slide. You can edit every slide before posting.", null, [<strong />])}</div>
        ) : (
          <>
            {/* Panorama — one wide photo across every slide */}
            <div className="border border-[var(--border)] p-3 space-y-3" data-testid="carousel-panorama">
              <div className="flex flex-wrap items-center gap-2">
                <GalleryHorizontal className="w-4 h-4" />
                {trRich("<0>Panorama</0>", null, [<span className="overline" />])}
                {!pano?.image ? (
                  <>
                    {trRich("<0>One wide photo split across all {total} slides, so it reads as one continuous picture.</0>", { total }, [<span className="text-[11px] text-[var(--ink-muted)]" />])}
                    <div className="ml-auto flex flex-wrap gap-2">
                      {panoOptions.length > 0 && (
                        <select
                          className={`${INPUT} max-w-[220px]`}
                          value=""
                          aria-label={tr("Use a photo already added as the panorama")}
                          data-testid="carousel-panorama-select"
                          onChange={(e) => e.target.value && choosePanorama(e.target.value)}
                        >
                          <option value="">{tr("Use a photo already added…")}</option>
                          {panoOptions.map((o) => (
                            <option key={o.url} value={o.url}>{o.label}</option>
                          ))}
                        </select>
                      )}
                      <button type="button" data-testid="carousel-panorama-new" className={BTN_SECONDARY} onClick={() => openImageDialog("panorama")}>
                        <ImagePlus className="w-3.5 h-3.5" />{" "}{tr("Add panorama photo")}</button>
                    </div>
                  </>
                ) : (
                  <div className="ml-auto flex flex-wrap gap-2">
                    <button type="button" data-testid="carousel-panorama-fit" className={BTN_SECONDARY} onClick={() => setPanorama({ ...pano, ...FIT_WIDTH })}>
                      <MoveHorizontal className="w-3.5 h-3.5" />{" "}{tr("Fit to width")}</button>
                    <button type="button" className={BTN_SECONDARY} onClick={() => openImageDialog("panorama")}>
                      <ImagePlus className="w-3.5 h-3.5" />{" "}{tr("Change photo")}</button>
                    <button type="button" data-testid="carousel-panorama-remove" className={BTN_SECONDARY} onClick={() => setPanorama(null)}>
                      <X className="w-3.5 h-3.5" />{" "}{tr("Remove panorama")}</button>
                  </div>
                )}
              </div>
              {pano?.image && (
                <>
                  {panoStrip && panoAspect ? (
                    <div className="overflow-x-auto">
                      <PhotoStage
                        contentW={stripWidth(total)}
                        contentH={SLIDE_H}
                        marginX={PANO_MARGIN_X}
                        marginY={PANO_MARGIN_Y}
                        scale={PANO_STAGE_WIDTH / (stripWidth(total) + 2 * PANO_MARGIN_X)}
                        rect={panoStrip}
                        src={ready(pano.image)}
                        unitW={stripWidth(total)}
                        minW={stripWidth(total) * MIN_ZOOM}
                        maxW={stripWidth(total) * MAX_ZOOM}
                        viewports={slides.map((s, i) => ({ key: i, left: i * SLIDE_W, top: 0, width: SLIDE_W, height: SLIDE_H, node: renderSlide(s, i) }))}
                        testid="carousel-panorama-stage"
                        onChange={(r) => setPanorama({ ...pano, ...rectToPlacement(r, stripWidth(total)) })}
                      />
                    </div>
                  ) : (
                    <div className="text-[11px] text-[var(--ink-muted)] inline-flex items-center gap-1">
                      <Loader2 className="w-3 h-3 animate-spin" />{" "}{tr("Loading panorama…")}</div>
                  )}
                  <div className="text-[11px] text-[var(--ink-muted)]">{tr("Drag a corner to resize (the shape stays), drag the photo to move it. Fit to width spreads it edge to edge across all {total} slides.", { total })}</div>
                  <div className="max-w-md">
                    <label className="overline mb-1.5 block" htmlFor="carousel-panorama-credit">{tr("Panorama credit")}</label>
                    <input
                      id="carousel-panorama-credit"
                      data-testid="carousel-panorama-credit"
                      className={INPUT}
                      value={pano.credit || ""}
                      maxLength={120}
                      placeholder={panoramaCredit({ ...pano, credit: "" }, article) || tr("e.g. Photo: Gringo Gazette")}
                      onChange={(e) => setPanorama({ ...pano, credit: e.target.value })}
                    />
                  </div>
                </>
              )}
            </div>

            {/* Slide strip — every slide rendered at full size, shown scaled */}
            <div className="flex gap-3 overflow-x-auto pb-2" data-testid="carousel-strip">
              {slides.map((s, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => setSel(i)}
                  aria-label={tr("Edit slide {value}", { value: i + 1 })}
                  className="shrink-0 text-left"
                  style={{
                    width: THUMB_W, height: THUMB_W * (SLIDE_H / SLIDE_W), overflow: "hidden",
                    outline: i === sel ? `3px solid ${LIME}` : "1px solid var(--border)",
                    outlineOffset: i === sel ? 2 : 0,
                  }}
                >
                  <div
                    ref={(el) => {
                      slideRefs.current[i] = el;
                    }}
                    style={{ width: SLIDE_W, height: SLIDE_H, transform: `scale(${THUMB_W / SLIDE_W})`, transformOrigin: "top left" }}
                  >
                    {renderSlide(s, i)}
                  </div>
                </button>
              ))}
            </div>

            <div className="grid gap-6 md:grid-cols-2">
              {/* Selected slide */}
              <div className="space-y-4 min-w-0">
                {current && (
                  <SlideEditor
                    slide={current}
                    index={sel}
                    total={total}
                    article={article}
                    carouselStyle={style}
                    imageUrl={slideImage(current, article)}
                    importing={importSource.isPending}
                    onChange={(patch) => edit(updateSlide(slides, sel, patch))}
                    onPickImage={pickImage}
                    onNewImage={() => openImageDialog("slide")}
                    panorama={pano?.image ? pano : null}
                    photoSrc={ready(slideImage(current, article))}
                    photoAspect={aspects[slideImage(current, article)] || 0}
                    slideNode={renderSlide(current, sel)}
                    badge={{
                      chip: chipText,
                      hidden: chipHidden,
                      auto: autoChip,
                      options: settings?.card_top_chips || [],
                      onChange: ({ chip: c, hidden }) => {
                        if (c !== undefined) setChipText(c);
                        if (hidden !== undefined) setChipHidden(hidden);
                        setDirty(true);
                      },
                    }}
                    onMove={(dir) => {
                      edit(moveSlide(slides, sel, dir));
                      setSel(Math.min(Math.max(sel + dir, 0), total - 1));
                    }}
                    onRemove={() => edit(removeSlide(slides, sel))}
                  />
                )}
                <div className="flex items-center gap-2 border-t border-[var(--border)] pt-3">
                  <label className="overline" htmlFor="carousel-new-kind">{tr("Add slide")}</label>
                  <select id="carousel-new-kind" className={`${INPUT} max-w-[160px]`} value={newKind} onChange={(e) => setNewKind(e.target.value)}>
                    {KINDS.filter((k) => k.id !== "cover").map((k) => (
                      <option key={k.id} value={k.id}>{k.label}</option>
                    ))}
                  </select>
                  <button
                    type="button"
                    className={BTN_SECONDARY}
                    disabled={total >= MAX_SLIDES}
                    onClick={() => {
                      const next = addSlide(slides, newKind);
                      edit(next);
                      const last = next[next.length - 1];
                      setSel(last && last.kind === "closing" && newKind !== "closing" ? next.length - 2 : next.length - 1);
                    }}
                  >
                    <Plus className="w-3.5 h-3.5" />{" "}{tr("Add")}</button>
                  <span className="text-[11px] text-[var(--ink-muted)]">{total}/{MAX_SLIDES}</span>
                </div>
              </div>

              {/* Post */}
              <div className="space-y-3 min-w-0">
                <div className="overline">{tr("Post to Instagram")}</div>
                {igPages.length === 0 ? (
                  <div className="text-xs text-[var(--ink-muted)] italic">{tr("No Instagram account is connected. Connect one in Settings → Social connections.")}</div>
                ) : (
                  <>
                    <select className={INPUT} value={pageId} onChange={(e) => setPageId(e.target.value)} aria-label={tr("Instagram account")}>
                      {igPages.map((p) => (
                        <option key={p.page_id} value={p.page_id}>
                          {p.ig_username ? `@${p.ig_username}` : p.page_name}
                        </option>
                      ))}
                    </select>
                    <div>
                      <label className="overline mb-1.5 block" htmlFor="carousel-caption">{tr("Caption")}</label>
                      <textarea id="carousel-caption" rows={5} className={TEXTAREA} value={caption} onChange={(e) => setCaption(e.target.value)} />
                    </div>
                    <ScheduleControls enabled={schedEnabled} setEnabled={setSchedEnabled} isoValue={schedIso} setIsoValue={setSchedIso} />
                    {dirty && <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 px-3 py-2">{tr("Save your changes before posting.")}</div>}
                    {!dirty && problem && <div className="text-[11px] text-[var(--ink-muted)]">{problem}</div>}
                    <button
                      type="button"
                      data-testid="carousel-post"
                      className={BTN_PRIMARY}
                      disabled={dirty || !!problem || share.isPending || status?.status === "publishing"}
                      onClick={() => share.mutate()}
                    >
                      {share.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Instagram className="w-3.5 h-3.5" />}
                      {schedEnabled ? tr("Schedule {total}-slide carousel", { total }) : tr("Post {total}-slide carousel", { total })}
                    </button>
                  </>
                )}
                {status?.status === "publishing" && (
                  <div className="flex items-center gap-2 text-xs text-[var(--ink-muted)]" data-testid="carousel-status">
                    <Loader2 className="w-3.5 h-3.5 animate-spin" /> {status.step || tr("Publishing")}…
                  </div>
                )}
                {status?.status === "published" && (
                  <div className="text-xs text-[var(--ink-muted)]" data-testid="carousel-status">{tr("Posted {value}", { value: status.slide_count ? tr("{slide_count} slides ", { slide_count: status.slide_count }) : "" })}
                    {status.permalink && (
                      <a className="text-[var(--accent)] underline" href={status.permalink} target="_blank" rel="noreferrer">{tr("View on Instagram")}</a>
                    )}
                  </div>
                )}
                {status?.status === "failed" && (
                  <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 px-3 py-2" data-testid="carousel-status">{tr("Instagram rejected the carousel: {error}", { error: status.error || tr("unknown error") })}
                  </div>
                )}
                <div className="border-t border-[var(--border)] pt-3">
                  <button type="button" data-testid="carousel-share-all" className={BTN_OUTLINE} onClick={shareAll} disabled={sharingAll}>
                    {sharingAll ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}{tr("Download / share all slides")}</button>
                  <div className="text-[11px] text-[var(--ink-muted)] mt-1">{tr("On a phone this opens the share sheet with every slide, for posting by hand.")}</div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </section>
  );
}
